// Pure job/line arithmetic and label-based cell lookup for the estimating
// screens. No React, no Supabase — see tools/verify-job-math.mjs. Only
// dependency is `xlsx` (SheetJS), used purely for cell-address helpers and
// reading already-parsed worksheet objects; no network, no DOM.
import * as XLSX from "xlsx/xlsx.mjs";

// The exact label every estimating template's pre-markup subtotal cell
// carries in column A — see templates/estimating/build_unit_type_floor_plan_a.py
// (row 37) and build_custom_line_item.py (row 39). Located by this label,
// never a fixed address, because the two templates put it on different rows.
export const SUBTOTAL_LABEL = "Subtotal (material + labor):";

// Locates the cell immediately to the right of the one exact-match label
// cell in a worksheet, scanning every cell (not just column A, so a future
// template that shifts columns still works). Throws if the label is
// missing or appears more than once — a duplicated or absent label is
// always a template defect, never something to silently pick one of or
// skip (spec section 5: "Template missing its subtotal row, or with two:
// that line errors and the proposal is blocked").
export function findLabelValueAddr(ws, label) {
  if (!ws || !ws["!ref"]) throw new Error("Sheet has no data.");
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const matches = [];
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c })];
      if (cell && typeof cell.v === "string" && cell.v.trim() === label) {
        matches.push({ r, c });
      }
    }
  }
  if (matches.length === 0) {
    throw new Error(`No cell labelled exactly "${label}" was found.`);
  }
  if (matches.length > 1) {
    throw new Error(`Found ${matches.length} cells labelled exactly "${label}"; expected exactly one.`);
  }
  const { r, c } = matches[0];
  return XLSX.utils.encode_cell({ r, c: c + 1 });
}

export function findSubtotalAddr(ws) {
  return findLabelValueAddr(ws, SUBTOTAL_LABEL);
}

// Mirrors src/ExcelImporter.js's evalFormulasWithReport: the main sheet is
// the one named after the template, falling back to the workbook's first
// sheet. Kept here (not imported from ExcelImporter.js) so jobMath.js has
// zero dependency on that file and cannot be affected by anything that
// touches it.
export function resolveMainSheet(wb, template) {
  const name = (template?.name && wb.Sheets[template.name]) ? template.name : wb.SheetNames[0];
  return wb.Sheets[name];
}

// Reads the numeric subtotal out of evalFormulasWithReport's
// {cellAddr: displayString} result map, using the label to find the cell.
export function readSubtotalFromResults(ws, evalResults) {
  const addr = findSubtotalAddr(ws);
  const raw = evalResults[addr];
  if (raw === undefined || raw === null || raw === "") {
    throw new Error(`Subtotal cell ${addr} evaluated to no value.`);
  }
  const num = parseFloat(String(raw).replace(/,/g, ""));
  if (Number.isNaN(num)) {
    throw new Error(`Subtotal cell ${addr} did not evaluate to a number (got ${JSON.stringify(raw)}).`);
  }
  return num;
}

// Math.round(n * 100) / 100 alone misrounds cases like 1.005 (which IEEE 754
// stores as 1.00499999999999989...), giving 1 instead of 1.01. A first fix
// nudged `n` by Number.EPSILON *before* scaling, but EPSILON is the gap
// between adjacent doubles at magnitude ~1 (~2.22e-16) — at realistic money
// magnitudes (e.g. n ~ 85) that gap is many times larger, so the nudge gets
// swallowed and the same half-cent misround comes right back (846.95*10/100
// must be 84.70, was landing on 84.69). Fixed by nudging AFTER scaling to
// cents instead, with a fixed constant: at the magnitudes this function
// actually sees (subtotal*pct/100 for subtotals up into the tens of
// thousands, so n*100 up to roughly 2,000,000), IEEE 754 double rounding
// error is bounded by roughly 4e-10 — 1e-9 safely absorbs that noise while
// staying far smaller than the 0.5-cent gap between any two real rounding
// outcomes, so it can only cancel float noise, never change a legitimate
// result. Assumes non-negative money values (this codebase never negates a
// subtotal/markup/total); do not reuse for signed amounts without re-checking.
export function round2(n) {
  return Math.round(n * 100 + 1e-9) / 100;
}

// A line's contribution to the job, pre-markup. Custom lines pass
// quantity 1 (enforced by the caller — this function never re-derives it).
export function computeLineSubtotal(reportSubtotal, quantity) {
  return reportSubtotal * quantity;
}

// Spreads `total` across `lines` in proportion to each line's lineSubtotal
// share of `subtotal`, in whole cents, then hands the leftover cents (from
// integer rounding of every other line) to the line with the LARGEST
// subtotal (the first one, on a tie), so the rows always sum EXACTLY to
// `total`. Final fix wave, item 10: the remainder used to go to the last
// line, which let a small last line print -$0.01 or $0.01 of pure rounding
// drift; on the largest line the same cent is invisible. If `subtotal` is 0
// (every line free or no lines have a positive subtotal) there is no
// proportional basis to spread from, so everything lands on the last line
// instead of dividing by zero.
export function spreadMarkup(lines, subtotal, total) {
  if (lines.length === 0) return [];
  const totalCents = Math.round(total * 100);
  if (subtotal <= 0) {
    return lines.map((l, i) => ({ ...l, markedUpAmount: i === lines.length - 1 ? totalCents / 100 : 0 }));
  }
  let largest = 0;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].lineSubtotal > lines[largest].lineSubtotal) largest = i;
  }
  let allocated = 0;
  const out = lines.map((l, i) => {
    if (i === largest) return null; // filled in below
    const shareCents = Math.round((l.lineSubtotal / subtotal) * totalCents);
    allocated += shareCents;
    return { ...l, markedUpAmount: shareCents / 100 };
  });
  out[largest] = { ...lines[largest], markedUpAmount: (totalCents - allocated) / 100 };
  return out;
}

// Rolls up a job's already-known-good lines (each {id, lineSubtotal}) into
// subtotal/markup/total, then spreads the markup back across the lines for
// the proposal PDF. Callers are responsible for excluding incomplete or
// missing lines before calling this — see resolveJobRollup in this file
// (added by the Sent-freeze task) for the policy that gates on that.
export function computeJobRollup(lines, markupPercent) {
  const subtotal = round2(lines.reduce((sum, l) => sum + l.lineSubtotal, 0));
  const markup = round2(subtotal * markupPercent / 100);
  const total = round2(subtotal + markup);
  const marked = spreadMarkup(lines, subtotal, total);
  return { subtotal, markup, total, lines: marked };
}

// A job is frozen once it has been marked Sent. Checked on every edit path
// (header, markup, quantity, add line, save) in both JobScreen and App.js's
// handlers: a sent bid is never edited, only Revised into a new draft.
// `frozen` alone also counts, so a row whose status was somehow lost still
// never reprices or accepts edits.
export function isJobFrozen(job) {
  return !!job && (job.status === "sent" || !!job.frozen);
}

export const SENT_WITHOUT_FIGURES_MESSAGE = "This job is marked Sent but its sent figures are missing, so no total is shown. Revise it to price it again.";

// The single decision point for "what does this job's total actually say
// right now": if the job is frozen (status "sent"), its frozen figures are
// returned UNCONDITIONALLY, never recomputed from live report/rate data,
// no matter what changed since (spec section 2: "Sent bids never
// reprice"). Otherwise, any line that isn't "ok" blocks a total from being
// produced at all (never silently treated as $0), as does a stored markup
// that doesn't parse (never a "$NaN" total; this check used to live inline
// in App.js's computeJobLineDetails), and a clean job is rolled up via
// computeJobRollup.
export function resolveJobRollup(job, lineResults) {
  if (job.frozen) {
    return {
      incomplete: false,
      incompleteLineIds: [],
      subtotal: job.frozen.subtotal,
      markup: job.frozen.markup,
      total: job.frozen.total,
      lines: job.frozen.lines,
      isFrozen: true,
    };
  }
  // Fix round 1, minor 4: marked Sent but no frozen figures (bad data). It
  // must never fall through to a live reprice, so it has no total at all.
  if (isJobFrozen(job)) {
    return {
      incomplete: true,
      incompleteLineIds: [],
      subtotal: null, markup: null, total: null,
      lines: [],
      isFrozen: true,
      frozenError: SENT_WITHOUT_FIGURES_MESSAGE,
    };
  }
  const bad = lineResults.filter((l) => l.status !== "ok");
  const markupParsed = parseMarkupPercent(job.markupPercent ?? 20);
  if (bad.length > 0 || lineResults.length === 0 || !markupParsed.ok) {
    return {
      incomplete: true,
      incompleteLineIds: bad.map((l) => l.id),
      subtotal: null, markup: null, total: null,
      lines: lineResults,
      isFrozen: false,
    };
  }
  const rollup = computeJobRollup(lineResults.map((l) => ({ id: l.id, lineSubtotal: l.lineSubtotal })), markupParsed.value);
  return { incomplete: false, incompleteLineIds: [], subtotal: rollup.subtotal, markup: rollup.markup, total: rollup.total, lines: rollup.lines, isFrozen: false };
}

// Builds the job markJobSent persists: status "sent" plus a frozen snapshot
// of the rollup and every line's figures, taken from line details computed
// for THIS job (App.js's computeJobLineDetails output: { lines: [{ line,
// status, lineSubtotal, markedUpAmount }], rollup }). Refuses an already
// frozen job, an incomplete rollup, and details whose lines don't match the
// job's current lines (id, order, takeoff, quantity), so a snapshot can never
// freeze figures computed for a different version of the job. Each frozen
// line keeps kind and reportId so the sent job can still show its takeoffs
// (read-only) and render custom lines correctly. proposalPath is null here;
// sendJobWithProposal (Task 7) sets it in the same write that marks the job
// sent, once the proposal PDF has uploaded.
export function buildSentJob(job, details) {
  if (isJobFrozen(job)) return { ok: false, error: "This job has already been sent." };
  const rollup = details?.rollup;
  if (!rollup || rollup.incomplete) {
    return { ok: false, error: "Cannot mark this job Sent. One or more lines is incomplete or missing its takeoff." };
  }
  const jobLines = job.lines || [];
  const detailLines = details.lines || [];
  const matches = jobLines.length === detailLines.length && jobLines.every((l, i) => {
    const d = detailLines[i];
    return d?.status === "ok" && d.line?.id === l.id && d.line?.reportId === l.reportId &&
      (d.line?.quantity ?? 1) === (l.quantity ?? 1) && Number.isFinite(d.lineSubtotal);
  });
  if (!matches) {
    return { ok: false, error: "The job changed while its totals were being worked out. Try Mark Sent again." };
  }
  const frozen = {
    subtotal: rollup.subtotal,
    markup: rollup.markup,
    total: rollup.total,
    lines: detailLines.map((ld) => ({
      id: ld.line.id,
      kind: ld.line.kind,
      reportId: ld.line.reportId,
      description: ld.line.description,
      quantity: ld.line.quantity,
      lineSubtotal: ld.lineSubtotal,
      markedUpAmount: ld.markedUpAmount,
    })),
    proposalPath: null, // set by sendJobWithProposal, in the write that marks the job sent
  };
  return { ok: true, sentJob: { ...job, status: "sent", frozen } };
}

// Builds everything a Revise needs so the new draft's takeoffs are
// INDEPENDENT copies, never the same report rows the sent job (and its
// already-generated frozen proposal) point at: editing a Rev 2 takeoff
// must never change the bid that was actually sent. Pure: takes
// already-loaded reports and an injectable id generator (so this is
// deterministically testable); the caller (App.js's reviseJob) is
// responsible for actually persisting the new report rows and the new job.
// A copy drops `_submitted`: the revision exists to be edited, and a
// submitted copy could never be opened for editing.
export function buildRevisedJobCopies(job, reportsById, idGenerator) {
  const newReports = [];
  const revision = (job.revision || 1) + 1;
  const newLines = (job.lines || []).map((line) => {
    const oldReport = line.reportId ? reportsById[line.reportId] : null;
    // Takeoff already missing: nothing to copy. Fix round 1: the reportId is
    // cleared rather than kept, so the revision never points at the sent
    // job's takeoff id; the line resolves "missing" as before.
    if (!oldReport) return { ...line, reportId: null };
    const newReportId = idGenerator();
    const { _submitted, ...rest } = oldReport;
    newReports.push({ ...rest, id: newReportId, reportTitle: revisionTitle(oldReport.reportTitle, revision) });
    return { ...line, id: idGenerator(), reportId: newReportId };
  });
  const revisedJob = {
    ...job,
    id: idGenerator(),
    status: "draft",
    frozen: null,
    revisionOf: job.id,
    revision,
    lines: newLines,
  };
  return { revisedJob, newReports };
}

// Task 7: the one function that combines a job's own lines (which always
// carry description/quantity, live or frozen) with a rollup's lines (which
// always carry id + markedUpAmount, live or frozen) into the flat shape
// src/proposalPdf.js renders. Neither computeJobRollup's nor
// resolveJobRollup's own `.lines` carry description/quantity by themselves;
// this is the one place the two get joined, by id, so the PDF generator
// never has to know the difference between a live job and a frozen one.
// A job line with no matching rollup line gets amount null; buildProposal
// below refuses that rather than letting it print as $0.00.
export function toProposalLines(jobLines, rollupLines) {
  return (jobLines || []).map((jl) => {
    const matched = (rollupLines || []).find((rl) => rl.id === jl.id);
    return {
      description: jl.description || "Line item",
      quantity: jl.quantity ?? 1,
      amount: matched ? matched.markedUpAmount : null,
    };
  });
}

// Task 7: the gate in front of every proposal PDF, preview or sent. Takes
// the job's lines and a rollup (resolveJobRollup's output, live or frozen)
// and returns { ok: true, proposal: { lines, total } } or { ok: false,
// error }. Refuses an incomplete rollup, a job with no lines, any line
// without a finite amount (never printed as $0.00), and lines that do not
// add up to the total to the cent (markup is spread across the lines by
// spreadMarkup and is never its own line, so they always should).
export function buildProposal(jobLines, rollup) {
  if (!rollup || rollup.incomplete || !Number.isFinite(rollup.total)) {
    return { ok: false, error: "Cannot create the proposal. One or more lines is incomplete or missing its takeoff." };
  }
  const lines = toProposalLines(jobLines, rollup.lines);
  if (lines.length === 0) return { ok: false, error: "Cannot create the proposal. This job has no lines." };
  const missing = lines.find((l) => !Number.isFinite(l.amount));
  if (missing) return { ok: false, error: `Cannot create the proposal. Line "${missing.description}" has no amount.` };
  const sumCents = lines.reduce((s, l) => s + Math.round(l.amount * 100), 0);
  if (sumCents !== Math.round(rollup.total * 100)) {
    return { ok: false, error: "Cannot create the proposal. The line amounts do not add up to the total." };
  }
  return { ok: true, proposal: { lines, total: rollup.total } };
}

// Task 7 (ruling R20): the order of Mark Sent once a proposal PDF is part
// of it. All I/O is injected, so this is tested without a browser or
// Supabase (tools/verify-job-math.mjs); App.js's markJobSentTurn supplies
// the real calls. The database trigger forbids any change to
// data->'frozen' once a job is frozen, so frozen.proposalPath has to be in
// the one write that marks the job sent. Hence:
//   1. makePdf() builds the PDF from the frozen figures;
//   2. uploadPdf(blob) stores it and returns { path, error };
//   3. only if that worked, checkBeforeWrite() re-checks the stored row
//      (App.js's storedJobBlockReason) and writeJob(job) writes the job
//      once, with status sent and frozen.proposalPath = path.
// Any failure before the upload leaves nothing behind. When the re-check
// refused or threw, the job write never ran, so the PDF is removed straight
// away. When the job write itself failed, the stored row is re-read first
// (readStoredJob): if it already points at this PDF the write landed after
// all (the response was lost), which is a success; if it cannot be read the
// PDF is kept, because deleting a file a sent job may point at is far worse
// than an orphan file; otherwise removePdf(path) deletes it. A failed
// removal is reported, never hidden. Every attempt uploads to its own path
// (proposalStoragePath), so a kept or orphaned file never blocks a retry.
//
// Resolves { ok: true, savedJob, recovered? } or { ok: false, stage, cause,
// check?, cleanup, cleanupCause?, path? }. stage is "pdf", "upload",
// "check" (the re-check threw), "blocked" (the re-check refused; `check`
// is its result) or "job". cleanup is "not-needed", "removed", "kept" or
// "failed". Never throws for a failure it knows about.
export async function sendJobWithProposal({ sentJob, makePdf, uploadPdf, checkBeforeWrite, writeJob, readStoredJob, removePdf }) {
  let blob;
  try {
    blob = makePdf();
  } catch (err) {
    return { ok: false, stage: "pdf", cause: err, cleanup: "not-needed" };
  }
  let path = null;
  let uploadError = null;
  try {
    const res = await uploadPdf(blob);
    path = res?.path || null;
    uploadError = res?.error || null;
  } catch (err) {
    uploadError = err;
  }
  if (!uploadError && !path) uploadError = new Error("The upload returned no storage path.");
  if (uploadError) return { ok: false, stage: "upload", cause: uploadError, cleanup: "not-needed" };

  const jobToWrite = { ...sentJob, frozen: { ...sentJob.frozen, proposalPath: path } };
  let stage = null;
  let cause = null;
  let check = null;
  try {
    check = await checkBeforeWrite();
  } catch (err) {
    stage = "check";
    cause = err;
  }
  if (!stage && check?.reason) stage = "blocked";
  if (!stage) {
    try {
      const { data, error } = await writeJob(jobToWrite);
      if (data) return { ok: true, savedJob: data };
      stage = "job";
      cause = error || new Error("The job was not saved.");
    } catch (err) {
      stage = "job";
      cause = err;
    }
  }

  const failure = { ok: false, stage, cause, check, path };
  // Fix round 1: only a job write that was attempted can have landed.
  if (stage === "job") {
    let stored;
    try {
      stored = await readStoredJob();
    } catch (err) {
      stored = { data: null, error: err };
    }
    if (stored?.error) return { ...failure, cleanup: "kept", cleanupCause: stored.error };
    if (stored?.data?.frozen?.proposalPath === path) return { ok: true, savedJob: stored.data, recovered: true };
  }
  try {
    const { error } = await removePdf(path);
    if (error) return { ...failure, cleanup: "failed", cleanupCause: error };
  } catch (err) {
    return { ...failure, cleanup: "failed", cleanupCause: err };
  }
  return { ...failure, cleanup: "removed" };
}

// Task 7: the alert for a Mark Sent that did not go through, from
// sendJobWithProposal's result. For "blocked", causeMessage is the stored
// check's own message (App.js's storedBlockMessage). Only a write known not
// to have happened may say the job was not changed.
export function markSentFailureMessage({ stage, causeMessage, cleanup, cleanupCauseMessage, path }) {
  const cause = causeMessage ? ` (${causeMessage})` : "";
  if (stage === "pdf") return `Could not create the proposal PDF, so the job was not marked Sent.${cause}`;
  if (stage === "upload") return `Could not save the proposal PDF, so the job was not marked Sent.${cause}`;
  const head = `Failed to mark the job sent.${cause}`;
  if (cleanup === "kept") {
    return `${head} Could not confirm whether the job was saved, so the proposal PDF uploaded for it was kept. Reopen the job to see whether it is marked Sent.`;
  }
  // "blocked" usually means the bid was sent elsewhere, so the stored job
  // did change, just not by this attempt.
  const notChanged = stage === "blocked" ? " This attempt did not change the job" : " The job was not changed";
  if (cleanup === "failed") {
    const why = cleanupCauseMessage ? ` (${cleanupCauseMessage})` : "";
    return `${head}${notChanged}. The proposal PDF uploaded for it could not be removed${why} and is still in storage at ${path}.`;
  }
  return `${head}${notChanged}, and the proposal PDF uploaded for it was removed.`;
}

// Fix round 1 (ruling R21): every Mark Sent attempt uploads to its own path.
// A fixed path wedged a revision for good once one attempt left its file
// behind (a lost upload response, or a cleanup that failed): upsert is off,
// so every retry was refused as "already exists". frozen.proposalPath
// records whichever path the successful attempt used.
export function proposalStoragePath(projectId, jobId, revision, attemptId) {
  if (!projectId || !jobId || !attemptId) throw new Error("A proposal storage path needs a project, a job and an attempt id.");
  return `${projectId}/proposals/${jobId}-R${revision || 1}-${attemptId}.pdf`;
}

// Fix round 1 (ruling R22): what a new line is called on the proposal until
// the estimator renames it. Unit type lines drop the "Unit Type - " prefix
// ("Floor Plan A"); custom lines start as "Custom area" and take the
// takeoff's own Description once it is saved (syncCustomLineDescription).
export const CUSTOM_LINE_DEFAULT_DESCRIPTION = "Custom area";

export function defaultLineDescription(template, kind) {
  if (kind === "custom") return CUSTOM_LINE_DEFAULT_DESCRIPTION;
  const name = String(template?.name || "").trim();
  return name.replace(/^unit\s*type\s*[-:]\s*/i, "").trim() || name || "Untitled line";
}

// The takeoff field a custom line's description comes from: the required
// field labelled "Description" (Custom Line Item template).
function findDescriptionField(template) {
  const allSections = template?.isMultiSheet
    ? (template.sheets || []).flatMap((sh) => sh.sections || [])
    : (template?.sections || []);
  return allSections.flatMap((sec) => sec.fields || [])
    .find((f) => String(f.label || "").replace(/:\s*$/, "").trim().toLowerCase() === "description") || null;
}

// Fix round 1 (ruling R22): after a custom line's takeoff is saved, the
// line takes the takeoff's Description, but only while the line still has
// a default description (empty, "Custom area", or the template's name). A
// description the estimator typed is never overwritten. Returns the same
// job object when nothing changes, so callers can skip the save.
export function syncCustomLineDescription(job, report, template) {
  const field = findDescriptionField(template);
  const value = field ? String(report?.[field.id] ?? "").trim() : "";
  if (!job || !report || !value) return job;
  const defaults = new Set(["", CUSTOM_LINE_DEFAULT_DESCRIPTION, String(template?.name || "").trim()]);
  let changed = false;
  const lines = (job.lines || []).map((l) => {
    if (l.kind !== "custom" || l.reportId !== report.id) return l;
    if (!defaults.has(String(l.description ?? "").trim()) || l.description === value) return l;
    changed = true;
    return { ...l, description: value };
  });
  return changed ? { ...job, lines } : job;
}

// Fix round 1 (ruling R22): the estimator's edit to one line's description.
export function applyLineDescription(job, lineId, raw) {
  const value = String(raw ?? "").trim();
  if (!value) return { ok: false, error: "A line needs a description. It is what the customer sees on the proposal." };
  if (!(job?.lines || []).some((l) => l.id === lineId)) return { ok: false, error: "That line could not be found on this job." };
  return { ok: true, job: { ...job, lines: job.lines.map((l) => (l.id === lineId ? { ...l, description: value } : l)) } };
}

// Fix round 1 (ruling R22): Mark Sent and the preview refuse a line whose
// description is empty or is still a raw template name, since that is what
// the customer would read. Fix round 2 (ruling R25): the "Custom area"
// placeholder is refused too. Returns the message naming the line, or null.
export function lineDescriptionProblem(lines, templateNames) {
  const raw = new Set((templateNames || []).map((n) => String(n || "").trim()).filter(Boolean));
  for (let i = 0; i < (lines || []).length; i++) {
    const d = String(lines[i].description ?? "").trim();
    if (!d) return `Line ${i + 1} has no description. Give it the description the customer should see, then try again.`;
    if (d === CUSTOM_LINE_DEFAULT_DESCRIPTION) return `Line ${i + 1} ("${d}") still has the placeholder description. Type the area's name, then try again.`;
    if (raw.has(d)) return `Line ${i + 1} ("${d}") still shows its template name. Change its description to what the customer should see, then try again.`;
  }
  return null;
}

// Fix round 1 (minor): the proposal number's base is the first job in the
// revisionOf chain, so Rev 1 and Rev 2 of one bid share a number. Walks the
// jobs it is given; a parent missing from the list ends the walk at the
// last id known. Guards against a cycle in bad data.
export function proposalBaseId(job, jobs) {
  if (!job) return null;
  const byId = new Map((jobs || []).map((j) => [j.id, j]));
  const seen = new Set([job.id]);
  let id = job.id;
  let cur = job;
  while (cur?.revisionOf && !seen.has(cur.revisionOf)) {
    id = cur.revisionOf;
    seen.add(id);
    cur = byId.get(id);
  }
  return id;
}

// The alert for a Revise that failed partway. stage is "takeoffs" (a takeoff
// copy did not save) or "job" (every copy saved but the new job did not).
// Either way App.js has already tried to delete every copy it created;
// leftoverTitles names the ones whose delete failed, since those still exist
// and the estimator has to know where they are. Only an empty list may say
// nothing was created. The sent job itself is never written by a Revise.
//
// Final fix wave, item 6: copiesKept means the job write failed or threw
// and the stored job could not be read back, so it is unknown whether the
// revision exists; its copies were deliberately kept (reviseLostResponseOutcome)
// and leftoverTitles lists all of them.
export function reviseFailureMessage({ stage, causeMessage, leftoverTitles, copiesKept = false }) {
  const what = stage === "job" ? "the new job could not be saved" : "a takeoff copy could not be saved";
  const cause = causeMessage ? ` (${causeMessage})` : "";
  const leftovers = (leftoverTitles || []).map((t) => `"${t || "Untitled Report"}"`);
  if (copiesKept) {
    if (leftovers.length === 0) {
      return `Could not confirm whether the revision was saved.${cause} Reload and open the Jobs list to see whether it is there. The sent job is unchanged.`;
    }
    return `Could not confirm whether the revision was saved.${cause} Its takeoff copies were kept, because a saved revision may point at them: ${leftovers.join(", ")}. ` +
      "Reload and open the Jobs list. If the revision is not there, delete these copies from the Reports tab. The sent job is unchanged.";
  }
  const aftermath = leftovers.length === 0
    ? " Nothing was created."
    : ` These takeoff copies could not be removed and are still in the Reports tab: ${leftovers.join(", ")}.`;
  return `Failed to create the revision: ${what}.${cause}${aftermath} The sent job is unchanged.`;
}

// Final fix wave, item 6: after a Revise's job write failed or threw, the
// stored row is read back before any takeoff copy is deleted (as
// sendJobWithProposal does for the proposal PDF). stored is getJob's
// { data, error } (or undefined when the read threw). "landed": the
// revision is stored after all (the response was lost), so the Revise
// succeeded and its copies stay. "kept": the row could not be read, so the
// copies are kept, since deleting takeoffs a saved revision points at is
// far worse than leaving copies behind. "cleanup": no such revision exists,
// so the copies may be deleted.
export function reviseLostResponseOutcome(stored, revisedJob) {
  if (!stored || stored.error) return "kept";
  const row = stored.data;
  if (row && row.id === revisedJob?.id && row.revisionOf === revisedJob?.revisionOf) return "landed";
  return "cleanup";
}

// Fix round 1, minor 3: a takeoff copy's title names its revision, so the
// Reports tab never shows two identical titles for a sent takeoff and its
// copy. An earlier " (Rev N)" suffix is replaced, never stacked.
export function revisionTitle(title, revision) {
  const base = String(title || "").replace(/ \(Rev \d+\)$/, "").trim() || "Untitled Report";
  return `${base} (Rev ${revision})`;
}

// Fix round 1, Important 1: the revision already made from this job, if
// any (the newest one, should duplicates already exist). reviseJob opens it
// instead of creating a second revision.
export function findExistingRevision(jobs, jobId) {
  let found = null;
  for (const j of jobs || []) {
    if (j.revisionOf !== jobId) continue;
    if (!found || (j.revision || 0) > (found.revision || 0)) found = j;
  }
  return found;
}

// Fix round 1, Important 2a (ruling R18): the freeze is also checked against
// the STORED rows just before a job write, because another tab or user may
// have marked the bid Sent since this screen loaded it. storedJob is the
// job's current database row (null if not saved yet). For a quantity
// change, takeoffReportId is the takeoff about to be written through and
// sentTakeoffIds is collectSentTakeoffIds over the stored job rows. Returns
// the refusal message, or null when the write may go ahead.
//
// Deliberately strict: any stored sent/frozen job refuses every write,
// including a status change. A future Won/Lost feature must relax this
// check and the jobs_refuse_frozen_edit trigger's status clause
// (supabase-schema.sql) together.
export function jobWriteBlockReason({ storedJob, intent, takeoffReportId, sentTakeoffIds }) {
  if (storedJob && isJobFrozen(storedJob)) return sentElsewhereMessage({ intent });
  if (takeoffReportId && sentTakeoffIds && sentTakeoffIds.has(takeoffReportId)) {
    return "This takeoff is part of a sent bid and can no longer be changed. Reopen the job to see the sent version.";
  }
  return null;
}

// Fix round 2: the refusal when the stored job turned out to be sent. Once
// App.js has put the stored sent version on screen (sentVersionShown), it
// no longer tells the user to reopen; if that replaced header or markup
// edits that were never saved (discardedUnsavedEdits), it says so.
export function sentElsewhereMessage({ intent, sentVersionShown = false, discardedUnsavedEdits = false }) {
  const head = intent === "markSent"
    ? "This bid has already been marked Sent elsewhere."
    : "This bid was marked Sent elsewhere.";
  const where = sentVersionShown ? " The sent version is now shown." : " Reopen it to see the sent version.";
  const lost = discardedUnsavedEdits ? " Your unsaved changes on this screen were not saved." : "";
  return head + where + lost;
}

// Fix round 2: whether the on-screen job carries header or markup edits the
// last saved copy doesn't have. Only these are held unsaved (line changes
// are saved by their own turns); status and ids are never user edits. Blank
// and missing count as the same value.
export function hasUnsavedJobEdits(localJob, savedJob) {
  if (!localJob || !savedJob) return false;
  const norm = (v) => (v === undefined || v === null ? "" : String(v));
  if ([...JOB_HEADER_FIELDS, "markupPercent"].some((k) => norm(localJob[k]) !== norm(savedJob[k]))) return true;
  // Task 7 fix round 1: line descriptions are held unsaved too.
  const savedDesc = new Map((savedJob.lines || []).map((l) => [l.id, norm(l.description)]));
  return (localJob.lines || []).some((l) => savedDesc.has(l.id) && savedDesc.get(l.id) !== norm(l.description));
}

// Fix round 1, minor 2: the sent job (if any) whose takeoffs use this
// template. Deleting that template would break the evidence behind a sent
// bid, so App.js's deleteTpl refuses and names the job.
export function findSentJobUsingTemplate(jobs, reports, templateId) {
  const templateByReport = new Map((reports || []).map((r) => [r.id, r._templateId]));
  for (const job of jobs || []) {
    if (!isJobFrozen(job)) continue;
    const ids = collectSentTakeoffIds([job]);
    for (const id of ids) if (templateByReport.get(id) === templateId) return job;
  }
  return null;
}

// Every takeoff report a frozen (sent) job points at, through its live
// lines or its frozen snapshot. These are the evidence behind a sent bid:
// App.js opens them read-only and refuses to save or delete them.
export function collectSentTakeoffIds(jobs) {
  const ids = new Set();
  for (const job of jobs || []) {
    if (!isJobFrozen(job)) continue;
    for (const line of job.lines || []) if (line.reportId) ids.add(line.reportId);
    for (const line of job.frozen?.lines || []) if (line.reportId) ids.add(line.reportId);
  }
  return ids;
}

// Every colored, required field (src/ExcelImporter.js's `required: true`,
// set on red-filled cells) that is empty on this report. Mirrors
// src/App.js's saveReport() missing-required check exactly, generalized to
// any template shape (single-sheet or multi-sheet).
export function findMissingRequiredFields(template, report) {
  if (!template) return [];
  const allSections = template.isMultiSheet
    ? (template.sheets || []).flatMap((sh) => sh.sections || [])
    : (template.sections || []);
  const requiredFields = allSections.flatMap((sec) => (sec.fields || []).filter((f) => f.required));
  return requiredFields.filter((f) => report?.[f.id] === undefined || report?.[f.id] === null || report?.[f.id] === "");
}

// A job line's status: "missing" when its takeoff report can no longer be
// found (deleted out from under the job — spec section 5), "incomplete"
// when the report exists but a required input is still empty, "ok"
// otherwise. A line is never silently treated as if it were $0 in either
// of the first two states — callers must gate on this before summing.
export function resolveLineStatus(line, report, template) {
  if (!report) return { status: "missing" };
  if (!template) return { status: "missing" };
  const missingFields = findMissingRequiredFields(template, report);
  if (missingFields.length > 0) return { status: "incomplete", missingFields };
  return { status: "ok" };
}

// The one required field every "Unit Type" template carries for its
// per-job multiplier (see templates/estimating/build_unit_type_floor_plan_a.py,
// row 41, "Number of units of this type on this job:"). Custom Line Item has
// no such field and this correctly returns null for it — a custom line's
// quantity is always 1 and is never written through to its report.
// Extracted to one place so line creation (which must seed it) and a
// later quantity edit (which must keep it in sync) can never disagree
// about which field this is.
export function findQuantityField(template) {
  const allSections = template?.isMultiSheet
    ? (template.sheets || []).flatMap((sh) => sh.sections || [])
    : (template?.sections || []);
  const requiredFields = allSections.flatMap((sec) => (sec.fields || []).filter((f) => f.required));
  return requiredFields.find((f) => /number of units/i.test(f.label)) || null;
}

// Final fix wave, item 1: a unit type line is priced at line.quantity, but
// the takeoff editor also shows (and saves) the "Number of units..." field.
// If the two disagree (40 typed in the takeoff, the job still says 1) the
// job must never be priced, previewed or sent: the line is incomplete, with
// this message, until they agree. Compared as exact text against the
// line's number (the form buildQuantityChange writes), so a takeoff value
// that is anything else fails closed. Returns the message, or null when
// they agree, the line is custom, or the template has no quantity field.
export function quantityDivergenceProblem(line, report, template) {
  if (!line || line.kind !== "unit_type") return null;
  const qtyField = findQuantityField(template);
  if (!qtyField) return null;
  const raw = report?.[qtyField.id];
  const lineQty = line.quantity ?? 1;
  if (String(raw ?? "") === String(lineQty)) return null;
  return `Its takeoff says "${String(raw ?? "")}" for the number of units, but this line says ${lineQty}. ` +
    "Open the takeoff and enter the number of units as a whole number, or change the quantity here, so they agree.";
}

// Final fix wave, item 1: after a takeoff that belongs to a draft job's unit
// type line is saved, the line takes the takeoff's quantity if it is a valid
// one (parseLineQuantity). An invalid value leaves the line alone, and it
// stays incomplete (quantityDivergenceProblem) with `problem` saying why.
// Returns { job, problem }; job is the same object when nothing changed.
export function syncUnitLineQuantity(job, report, template) {
  const qtyField = findQuantityField(template);
  if (!job || !report || !qtyField) return { job, problem: null };
  const targets = (job.lines || []).filter((l) => l.kind === "unit_type" && l.reportId === report.id);
  if (targets.length === 0) return { job, problem: null };
  const parsed = parseLineQuantity(report[qtyField.id]);
  if (!parsed.ok) return { job, problem: parsed.error };
  if (targets.every((l) => (l.quantity ?? 1) === parsed.value)) return { job, problem: null };
  const lines = job.lines.map((l) => (l.kind === "unit_type" && l.reportId === report.id ? { ...l, quantity: parsed.value } : l));
  return { job: { ...job, lines }, problem: null };
}

// Final fix wave, item 1: the first line computeJobLineDetails flagged with
// a message (a quantity mismatch or an evaluation error), for the Mark Sent
// and preview refusals. Null when no line carries one.
export function lineProblemMessage(detailLines) {
  const list = detailLines || [];
  for (let i = 0; i < list.length; i++) {
    const ld = list[i];
    if (ld.status !== "ok" && ld.error) return `Line ${i + 1} ("${ld.line?.description || "Untitled line"}"): ${ld.error}`;
  }
  return null;
}

// Validates a job line's quantity before it is ever written through to a
// report's "Number of units..." field or persisted on the job. Only a whole
// number >= 1 is valid: a job line always represents at least one physical
// unit, so 0/negative has no meaning, and a fractional value can't be
// written into the report's own text-typed required field without silently
// corrupting it. Deliberately never returns a fallback value on failure —
// callers must show `error` and refuse to persist, never substitute 1 the
// way `quantity || 1` used to (fix round 1, finding #1: that silently
// billed 0/empty/NaN as 1 and could write the literal string "NaN" into a
// report's required field).
export function parseLineQuantity(raw) {
  if (raw === null || raw === undefined) return { ok: false, value: null, error: "Quantity is required." };
  const trimmed = String(raw).trim();
  if (trimmed === "") return { ok: false, value: null, error: "Quantity is required." };
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return { ok: false, value: null, error: "Quantity must be a number." };
  if (!Number.isInteger(num)) return { ok: false, value: null, error: "Quantity must be a whole number." };
  if (num < 1) return { ok: false, value: null, error: "Quantity must be at least 1." };
  return { ok: true, value: num, error: null };
}

// Validates a job's markup percent before it is ever stored on
// activeJob.markupPercent or used to compute a rollup. Unlike quantity,
// decimals are legitimate (17.5% appears in this file's own hand-checked
// rollup tests), so this only rejects blank/non-numeric/negative input —
// never a fallback, for the same reason parseLineQuantity never falls back
// to 1 (fix round 1, finding #6: a raw, unparsed markup string used to get
// written straight into activeJob.markupPercent, and an invalid value used
// to compute straight through to a "$NaN" total).
export function parseMarkupPercent(raw) {
  if (raw === null || raw === undefined) return { ok: false, value: null, error: "Markup is required." };
  const trimmed = String(raw).trim();
  if (trimmed === "") return { ok: false, value: null, error: "Markup is required." };
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return { ok: false, value: null, error: "Markup must be a number." };
  if (num < 0) return { ok: false, value: null, error: "Markup cannot be negative." };
  return { ok: true, value: num, error: null };
}

// Fix round 2, findings #2/#3: the entire decision behind "what happens when
// a job line's quantity changes" moved here, out of App.js's
// changeJobLineQuantity, specifically so it is unit-testable without a
// browser and without any dependency on React state-updater timing (the
// bug this fix round exists to correct: App.js was reading `job`/`reports`
// back out of a setState updater callback expecting it to run
// synchronously, which React 18 does not guarantee — see this function's
// caller in App.js for the full story). Pure in, pure out: given a job, the
// reports/templates arrays to resolve against, a line id, and a raw
// quantity, this decides whether the change is even valid and, if so,
// builds the updated job (always) and the updated report (only for a
// unit_type line whose template has a quantity field to write through to) —
// it performs no I/O and mutates nothing it was handed.
//
// On failure returns only { ok: false, error } — deliberately no
// `updatedJob`/`updatedReport` keys at all, so a caller that forgets to
// check `ok` gets `undefined` (loud) instead of an old value quietly
// carried over from a previous call's shape.
export function buildQuantityChange(job, reports, templates, lineId, rawQuantity) {
  const parsed = parseLineQuantity(rawQuantity);
  if (!parsed.ok) return { ok: false, error: parsed.error };

  const line = (job?.lines || []).find((l) => l.id === lineId);
  if (!line) return { ok: false, error: "That line could not be found on this job." };

  const quantity = parsed.value;

  // Fix round 3, finding #e: a unit_type line whose report or template
  // can't be resolved is a hard error now, not a silent "ok, but nothing
  // to write through." Returning ok:true here used to let the job's own
  // line.quantity change while the report it's supposed to agree with
  // just never got touched — exactly the report==job disagreement this
  // whole feature exists to prevent, just arrived at a different way.
  let updatedReport = null;
  let previousReport = null;
  if (line.kind === "unit_type") {
    const report = (reports || []).find((r) => r.id === line.reportId);
    if (!report) return { ok: false, error: "This line's takeoff report could not be found." };
    const template = (templates || []).find((t) => t.id === report._templateId);
    if (!template) return { ok: false, error: "This line's takeoff template could not be found." };
    const qtyField = findQuantityField(template);
    // Final fix wave, item 8: a submitted takeoff is final (the Reports tab
    // never edits one), so its quantity is never rewritten from here.
    if (qtyField && report._submitted) {
      return { ok: false, error: "This line's takeoff has been submitted, so its number of units can no longer be changed. The quantity was not changed." };
    }
    if (qtyField) {
      updatedReport = { ...report, [qtyField.id]: String(quantity) };
      // Fix round 5: the report as it was, so a job save that fails after
      // updatedReport was written can put the takeoff back.
      previousReport = report;
    }
    // A unit_type template with no quantity field at all would be a
    // template defect, not a data-integrity problem this function is
    // responsible for catching — updatedReport stays null and only the
    // job's own line.quantity changes.
  }

  const updatedJob = { ...job, lines: job.lines.map((l) => (l.id === lineId ? { ...l, quantity } : l)) };
  return { ok: true, updatedJob, updatedReport, previousReport };
}

// Fix round 5, finding 3: the alert for "the takeoff's quantity was saved
// but the job save failed." reportRestore is "not-needed" (no takeoff was
// written), "restored" (the takeoff was put back to previousReport) or
// "failed" (the restore did not save either). Only the first two may say the
// quantity was not changed; "failed" names the line and both quantities,
// because the takeoff and the job now disagree.
export function quantityJobSaveFailureMessage({ line, newQuantity, causeMessage, reportRestore }) {
  const cause = causeMessage ? ` (${causeMessage})` : "";
  if (reportRestore !== "failed") {
    return `Failed to save the job with this quantity change.${cause} The quantity was not changed.`;
  }
  const name = line?.description || "Untitled line";
  return (
    `Failed to save the job with this quantity change.${cause} The takeoff could not be set back either, ` +
    `so line "${name}" is out of step: its takeoff now says ${newQuantity} but the job still says ${line?.quantity ?? 1}. ` +
    `Enter the quantity again to bring them back in line.`
  );
}

// Fix round 2: the decision behind "what does the job look like after a new
// line is added" moved here for the same reason as buildQuantityChange —
// testable without a browser. `idGenerator` is injected (never calls
// crypto.randomUUID() itself) so a test can assert on a deterministic id
// instead of a random one; App.js passes `() => crypto.randomUUID()`.
export function buildNewLine(job, template, kind, savedReportId, idGenerator) {
  // Fix round 1 (ruling R22): a proposal-ready default, never the raw
  // template name (defaultLineDescription).
  const line = { id: idGenerator(), kind, reportId: savedReportId, quantity: 1, description: defaultLineDescription(template, kind) };
  return { ...job, lines: [...(job?.lines || []), line] };
}

// Final fix wave, item 11: the project's jobs are loaded with the stored
// rows (loadState "ok"), still loading ("loading"), or failed to load
// ("error"). Until they are known, nothing can tell whether a takeoff or a
// template is behind a sent bid, so the Reports tab's Edit, Delete and
// Submit and the template Delete refuse with this message instead of
// assuming there are no sent jobs. A project without the Jobs module
// (jobsEnabled false, see projectHasEstimatingTemplates) cannot have a sent
// bid made in it, so it is never blocked: every other tenant's reports keep
// working even where the jobs table does not exist yet. Returns the
// message, or null when the action may go ahead.
export function jobsUnknownMessage(loadState, jobsEnabled) {
  if (!jobsEnabled || loadState === "ok") return null;
  if (loadState === "loading") {
    return "This project's jobs are still loading, so it is not yet known whether this is part of a sent bid. Nothing was changed. Try again in a moment.";
  }
  return "Could not load this project's jobs, so it is not known whether this is part of a sent bid. Nothing was changed. Reload the page and try again.";
}

// Final fix wave, item 9: the markup a new job starts with. Takes the rate
// catalog rows (getRateCatalogItemsOrThrow) and uses the markup entry
// flagged default, else the one named "Standard" (the entry the templates
// bake in, rateCellPatch.RATE_CELL_LABELS), else 20. An entry whose percent
// is not a finite number >= 0 is never used. Returns { percent, source }
// with source "default", "standard" or "fallback".
export const FALLBACK_MARKUP_PERCENT = 20;
export function defaultMarkupPercent(items) {
  const usable = (it) => typeof it?.data?.percent === "number" && Number.isFinite(it.data.percent) && it.data.percent >= 0;
  const markups = (items || []).filter((it) => it?.category === "markup" && usable(it));
  const flagged = markups.find((it) => it.data.default === true);
  if (flagged) return { percent: flagged.data.percent, source: "default" };
  const standard = markups.find((it) => it.name === "Standard");
  if (standard) return { percent: standard.data.percent, source: "standard" };
  return { percent: FALLBACK_MARKUP_PERCENT, source: "fallback" };
}

// Final fix wave, item 4 (ruling R27): which templates the estimating
// screens work with, by name. These are the same tests the Job screen has
// always used to offer "+ Add <unit type>" and "+ Add custom area"
// (App.js), now in one place. The Jobs module is only offered in a project
// that has at least one such template: without one, a job can never get a
// line, and every other tenant (ASTM, Proctor, ...) must never see a Jobs
// tab. Name-based on purpose: rateCellPatch.isEstimatingTemplate is more
// exact but needs every template's stored file downloaded and parsed on
// each project load, and would hide the tab whenever storage is slow or
// down; the name is already in the template list the app loads anyway.
export function isUnitTypeTemplate(template) {
  return /unit type/i.test(String(template?.name || ""));
}

export function isCustomLineTemplate(template) {
  return /custom line item/i.test(String(template?.name || ""));
}

export function projectHasEstimatingTemplates(templates) {
  return (templates || []).some((t) => isUnitTypeTemplate(t) || isCustomLineTemplate(t));
}

// Final fix wave, item 2: a job (and so its takeoffs) must never move to
// another project. A job read from the database carries projectId
// (supabase.js's rowToJob); upsertJob refuses, without writing, when that
// differs from the project it is asked to write into, e.g. a stale job
// screen left over from a sign-out and a sign-in to a different project. A
// brand-new job has no projectId yet and may be written. A write with no
// target project at all is refused too. Returns the message, or null.
export function jobProjectMismatch(job, projectId) {
  if (!projectId) return "No project is selected, so the job was not saved.";
  if (job?.projectId && job.projectId !== projectId) {
    return "This job belongs to another project, so it was not saved here. Reopen it from its own project's Jobs list.";
  }
  return null;
}

// Fix round 4: the only header fields JobScreen edits as free text. Each is
// held as a local draft in JobScreen and committed on blur/Enter through a
// queued turn in App.js, which applies it to the job that turn read at its
// start. Anything else (markup, lines, status, ids) has its own validated
// path and must never be written through this one.
export const JOB_HEADER_FIELDS = Object.freeze(["name", "property", "address", "contact"]);

export function applyJobHeaderField(job, key, value) {
  if (!JOB_HEADER_FIELDS.includes(key)) throw new Error(`"${key}" is not an editable job header field.`);
  return { ...job, [key]: value };
}
